/**
 * Spark Activation API (vercel-auth-functions)
 *
 * Handles the server-side activation step that sits between access code
 * validation and Supabase OTP.
 *
 * TODO: Vercel base URLs for stage / prod (only dev available at the moment)
 */
import { envSwitch } from './environmentUtils';

const VERCEL_BASE_URL = envSwitch({
  local: 'https://vercel-auth-functions.vercel.app',
  dev: 'https://vercel-auth-functions.vercel.app',
  qa: 'https://vercel-auth-functions.vercel.app',
  pr: 'https://vercel-auth-functions.vercel.app',
  stage: 'https://vercel-auth-functions.vercel.app',
  prod: 'https://vercel-auth-functions.vercel.app',
});

const getLob = (): 'wellness' => 'wellness';

interface ActivatePhoneParams {
  phone: string;
  accessCode: string;
  product: string;
}

interface ActivatePhoneResponse {
  success: boolean;
  message: string;
  limbix_user_id: string;
}

interface ActivateConfirmParams {
  supabaseUserId: string;
  accessToken: string;
}

/**
 * Calls POST /api/spark/{lob}/activate/confirm to flip user_app_entitlements.status → active.
 * Must be called AFTER supabase.auth.verifyOtp() succeeds.
 */
export const activateSparkConfirm = async (
  params: ActivateConfirmParams,
): Promise<void> => {
  const lob = getLob();
  const url = `${VERCEL_BASE_URL}/api/spark/${lob}/activate/confirm`;
  const payload = { supabase_user_id: params.supabaseUserId };

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.accessToken}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    console.error('[activateSparkConfirm] failed with status:', response.status);
    throw new Error(`Spark confirm failed: ${response.status}`);
  }
};

/**
 * Calls POST /api/spark/{lob}/activate/phone to register the user server-side.
 * Must be called BEFORE supabase.auth.signInWithOtp()
 */
export const activateSparkPhone = async (
  params: ActivatePhoneParams,
): Promise<ActivatePhoneResponse> => {
  const lob = getLob();
  const url = `${VERCEL_BASE_URL}/api/spark/${lob}/activate/phone`;
  const payload = {
    phone: params.phone,
    access_code: params.accessCode,
    product: params.product,
    line_of_business: lob,
  };

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    console.error('[activateSparkPhone] failed with status:', response.status);
    throw new Error(`Spark activation failed: ${response.status}`);
  }

  return response.json();
};

interface ActivateEmailParams {
  email: string;
  product: string;
  accessCode?: string;
  organizationSlug?: string;
}

interface ActivateEmailResponse {
  success: boolean;
  limbix_user_id: string;
}

/**
 * Calls POST /api/{product}/{lob}/activate/email to trigger a magic link email.
 *
 * Two flows:
 * - Access code flow: send { email, access_code }
 * - Direct access flow: send { email, organization_slug }
 */
export const activateEmail = async (
  params: ActivateEmailParams,
): Promise<ActivateEmailResponse> => {
  const lob = getLob();
  const url = `${VERCEL_BASE_URL}/api/${params.product}/${lob}/activate/email`;

  const payload: Record<string, string> = {
    email: params.email,
  };

  if (params.accessCode) {
    payload.access_code = params.accessCode;
  } else if (params.organizationSlug) {
    payload.organization_slug = params.organizationSlug;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    console.error('[activateEmail] failed:', response.status, body.error);
    throw new Error(body.error || `Email activation failed: ${response.status}`);
  }

  return response.json();
};
